
import { CompanyMemory, Interaction, InteractionType, Task, GlobalMemory, FullDossier, Company, AgentResult, CallAnalysisResult } from '../types';

// In-memory store for MVP (wordt gereset bij refresh, in prod -> DB/LocalStorage)
const MEMORY_STORE: Record<string, CompanyMemory> = {};
const TASK_STORE: Task[] = [];
let GLOBAL_MEMORY: GlobalMemory = { trainingNotes: [], lastUpdated: new Date().toISOString() };

// LocalStorage Sync Keys
const GLOBAL_MEM_KEY = 'bdat_global_memory';
const COMPANY_MEM_KEY_PREFIX = 'bdat_mem_';

type MemoryPersistenceAdapter = {
  loadMemory?: (companyId: string, projectId: string) => Promise<CompanyMemory | null>;
  saveMemory?: (memory: CompanyMemory) => Promise<void>;
};

export class MemoryService {
  private static persistenceAdapter: MemoryPersistenceAdapter | null = null;
  
  static configurePersistenceAdapter(adapter: MemoryPersistenceAdapter | null) {
      this.persistenceAdapter = adapter;
  }
  
  static loadGlobalMemory() {
      const saved = localStorage.getItem(GLOBAL_MEM_KEY);
      if (saved) {
          try { GLOBAL_MEMORY = JSON.parse(saved); } catch (e) { /* ignore */ }
      }
  }

  static getGlobalContext(): string {
      this.loadGlobalMemory();
      if (GLOBAL_MEMORY.trainingNotes.length === 0) return "";
      return `\n\n[GLOBAL GELEERDE LESSEN - PAS DIT TOE OP ALLES]:\n${GLOBAL_MEMORY.trainingNotes.map(n => `- ${n}`).join('\n')}\n`;
  }

  // NIEUW: Aggregeer patronen van ALLE bedrijven in dit project
  static getProjectWisdom(projectId: string): string {
      // 1. Loop door alle keys in localStorage die beginnen met prefix
      const wisdom = {
          successful: new Set<string>(),
          failed: new Set<string>(),
          objections: new Set<string>()
      };

      // Scan zowel in-memory als localStorage (voor persistentie)
      Object.keys(localStorage).forEach(key => {
          if (key.startsWith(COMPANY_MEM_KEY_PREFIX)) {
               try {
                   const mem: CompanyMemory = JSON.parse(localStorage.getItem(key) || '{}');
                   if (mem.projectId === projectId && mem.patterns) {
                       mem.patterns.successfulAngles.forEach(a => wisdom.successful.add(a));
                       mem.patterns.failedAngles.forEach(a => wisdom.failed.add(a));
                       mem.patterns.commonObjections.forEach(a => wisdom.objections.add(a));
                   }
               } catch(e) { /* ignore */ }
          }
      });

      // Voeg ook in-memory store toe (voor nog niet opgeslagen changes)
      Object.values(MEMORY_STORE).forEach(mem => {
           if (mem.projectId === projectId && mem.patterns) {
               mem.patterns.successfulAngles.forEach(a => wisdom.successful.add(a));
               mem.patterns.failedAngles.forEach(a => wisdom.failed.add(a));
               mem.patterns.commonObjections.forEach(a => wisdom.objections.add(a));
           }
      });

      if (wisdom.successful.size === 0 && wisdom.failed.size === 0) return "";

      return `
[HIVE MIND - PROJECT INSIGHTS]:
- WERKT GOED (DO MORE): ${Array.from(wisdom.successful).join(', ') || 'Nog geen data.'}
- WERKT NIET (AVOID): ${Array.from(wisdom.failed).join(', ') || 'Nog geen data.'}
- VEELVOORKOMENDE BEZWAREN: ${Array.from(wisdom.objections).join(', ') || 'Nog geen data.'}
`;
  }

  static addGlobalTraining(note: string) {
      this.loadGlobalMemory();
      GLOBAL_MEMORY.trainingNotes.push(note);
      GLOBAL_MEMORY.lastUpdated = new Date().toISOString();
      localStorage.setItem(GLOBAL_MEM_KEY, JSON.stringify(GLOBAL_MEMORY));
  }

  static getMemory(companyId: string, projectId: string): CompanyMemory {
    const key = `${companyId}-${projectId}`;
    // Try load from storage first if not in memory
    if (!MEMORY_STORE[key]) {
        const saved = localStorage.getItem(COMPANY_MEM_KEY_PREFIX + key);
        if (saved) {
            MEMORY_STORE[key] = JSON.parse(saved);
        } else {
            MEMORY_STORE[key] = {
                companyId,
                projectId,
                history: [],
                patterns: {
                successfulAngles: [],
                failedAngles: [],
                commonObjections: [],
                preferredTone: 'professional'
                },
                updatedAt: new Date().toISOString()
            };
        }
    }
    return MEMORY_STORE[key];
  }

  static saveMemory(companyId: string, projectId: string) {
      const key = `${companyId}-${projectId}`;
      if (MEMORY_STORE[key]) {
          localStorage.setItem(COMPANY_MEM_KEY_PREFIX + key, JSON.stringify(MEMORY_STORE[key]));
          void this.persistenceAdapter?.saveMemory?.(MEMORY_STORE[key]).catch((error) => {
              console.error('[Memory] Backend sync failed', error);
          });
      }
  }

  static hydrateMemory(companyId: string, projectId: string, memory: CompanyMemory) {
      const key = `${companyId}-${projectId}`;
      MEMORY_STORE[key] = {
          ...memory,
          companyId,
          projectId,
          updatedAt: new Date().toISOString()
      };
      this.saveMemory(companyId, projectId);
      return MEMORY_STORE[key];
  }

  static async syncMemoryFromBackend(companyId: string, projectId: string): Promise<CompanyMemory> {
      try {
          const remoteMemory = await this.persistenceAdapter?.loadMemory?.(companyId, projectId);
          if (remoteMemory) {
              return this.hydrateMemory(companyId, projectId, remoteMemory);
          }
      } catch (error) {
          console.warn('[Memory] Remote load failed, using local cache', error);
      }
      return this.getMemory(companyId, projectId);
  }

  static exportProjectMemories(project: { id: string; customData?: Company[] }): Record<string, CompanyMemory> {
      const memories: Record<string, CompanyMemory> = {};
      project.customData?.forEach((company) => {
          if (!company.id) return;
          memories[company.id] = this.getMemory(company.id, project.id);
      });
      return memories;
  }

  // NIEUW: Initialiseer geheugen voor een nieuw bedrijf
  static initMemory(companyId: string, projectId: string, source: string) {
      const memory = this.getMemory(companyId, projectId);
      // Check of er al een entry is. Zo niet, maak een 'created' entry.
      if (memory.history.length === 0) {
          this.addInteraction(companyId, projectId, {
              id: crypto.randomUUID(),
              type: 'system_log',
              direction: 'inbound',
              timestamp: new Date().toISOString(),
              summary: `Dossier aangemaakt vanuit ${source}`,
              sentiment: 'neutral'
          });
      }
  }

  static addInteraction(companyId: string, projectId: string, interaction: Interaction) {
    const memory = this.getMemory(companyId, projectId);
    
    // Voorkom dubbele entries (simpele check op timestamp/summary)
    const exists = memory.history.some(h => h.summary === interaction.summary && Math.abs(new Date(h.timestamp).getTime() - new Date(interaction.timestamp).getTime()) < 1000);
    if (exists) return memory;

    memory.history.unshift(interaction); // Add to top
    memory.updatedAt = new Date().toISOString();
    
    if (interaction.sentiment === 'negative' && interaction.summary) {
        memory.patterns.failedAngles.push(`Failed: ${new Date().toLocaleDateString()}`);
    }
    
    this.saveMemory(companyId, projectId);
    return memory;
  }

  static applyCallLearning(companyId: string, projectId: string, analysis: Partial<CallAnalysisResult>) {
      const memory = this.getMemory(companyId, projectId);
      const unique = (values: string[]) => Array.from(new Set(values.map((item) => item.trim()).filter(Boolean)));

      memory.patterns.successfulAngles = unique([
          ...memory.patterns.successfulAngles,
          ...(analysis.successfulAngles || [])
      ]);

      memory.patterns.failedAngles = unique([
          ...memory.patterns.failedAngles,
          ...(analysis.failedAngles || [])
      ]);

      memory.patterns.commonObjections = unique([
          ...memory.patterns.commonObjections,
          ...(analysis.commonObjections || []),
          ...(analysis.objections || [])
      ]);

      memory.updatedAt = new Date().toISOString();
      this.saveMemory(companyId, projectId);
      return memory;
  }
  
  static updateInteraction(companyId: string, projectId: string, interactionId: string, newSummary: string) {
      const memory = this.getMemory(companyId, projectId);
      const interaction = memory.history.find(h => h.id === interactionId);
      if (interaction) {
          interaction.summary = newSummary;
          this.saveMemory(companyId, projectId);
      }
      return memory;
  }

  static updatePatterns(companyId: string, projectId: string, newPatterns: CompanyMemory['patterns']) {
      const memory = this.getMemory(companyId, projectId);
      memory.patterns = newPatterns;
      this.saveMemory(companyId, projectId);
      return memory;
  }

  static getTasks(companyId: string): Task[] {
    return TASK_STORE.filter(t => t.companyId === companyId && t.status === 'pending');
  }

  static getAllPendingTasks(): Task[] {
    return TASK_STORE.filter(t => t.status === 'pending');
  }

  static addTask(task: Task) {
    TASK_STORE.push(task);
  }
  
  static updateTaskStatus(taskId: string, status: 'pending' | 'completed') {
    const task = TASK_STORE.find(t => t.id === taskId);
    if (task) task.status = status;
  }

  static getContextSummary(companyId: string, projectId: string): string {
    const mem = this.getMemory(companyId, projectId);
    const globalContext = this.getGlobalContext();
    const projectWisdom = this.getProjectWisdom(projectId); // Voeg de hive mind toe

    const patterns = `
[SPECIFIEKE PATRONEN VOOR DIT BEDRIJF]:
- VOORKEURSTOON: ${mem.patterns.preferredTone}
- SUCCESVOL: ${mem.patterns.successfulAngles.join(', ') || 'Nog geen data'}
- GEFAALD: ${mem.patterns.failedAngles.join(', ') || 'Nog geen data'}
- BEZWAREN: ${mem.patterns.commonObjections.join(', ') || 'Nog geen data'}
`;

    const historyText = mem.history.length === 0
        ? "Geen eerdere interacties."
        : mem.history.map(h => `[${h.type.toUpperCase()}] ${h.direction}: ${h.summary}`).join('\n');
        
    return `${globalContext}\n${projectWisdom}\n${patterns}\n\n[SPECIFIEKE GESCHIEDENIS]:\n${historyText}`;
  }
  static exportDossier(companyId: string, projectId: string, companyData: Company, researchData: AgentResult[]): FullDossier {
      const memory = this.getMemory(companyId, projectId);
      const tasks = this.getTasks(companyId);
      
      return {
          version: "1.0",
          company: companyData,
          memory: memory,
          researchResults: researchData,
          tasks: tasks,
          exportedAt: new Date().toISOString()
      };
  }

  static importDossier(dossier: FullDossier, projectId: string): { memory: CompanyMemory, tasks: Task[] } {
      const companyId = dossier.company.id;
      
      // 1. Merge Memory
      const currentMemory = this.getMemory(companyId, projectId);
      
      // Merge history (avoid duplicates based on ID or content)
      const newInteractions = dossier.memory.history.filter(h =>
          !currentMemory.history.some(ch => ch.id === h.id || (ch.summary === h.summary && ch.timestamp === h.timestamp))
      );
      currentMemory.history = [...newInteractions, ...currentMemory.history].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      
      // Merge Patterns (Union of sets logic)
      const uniqueSuccess = new Set([...currentMemory.patterns.successfulAngles, ...dossier.memory.patterns.successfulAngles]);
      const uniqueFailed = new Set([...currentMemory.patterns.failedAngles, ...dossier.memory.patterns.failedAngles]);
      const uniqueObjections = new Set([...currentMemory.patterns.commonObjections, ...dossier.memory.patterns.commonObjections]);
      
      currentMemory.patterns.successfulAngles = Array.from(uniqueSuccess);
      currentMemory.patterns.failedAngles = Array.from(uniqueFailed);
      currentMemory.patterns.commonObjections = Array.from(uniqueObjections);
      
      this.saveMemory(companyId, projectId);

      // 2. Merge Tasks
      dossier.tasks.forEach(task => {
          if (!TASK_STORE.find(t => t.id === task.id)) {
              // Ensure task is linked to current project ID if we are importing into a different project context?
              // Ideally keep original ID but update projectId
              TASK_STORE.push({ ...task, projectId });
          }
      });

      return { memory: currentMemory, tasks: dossier.tasks };
  }
}
